import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import { Shield, Edit, AlertCircle } from "lucide-react";
import PermissionMatrix from "./PermissionMatrix";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/enterprise`;

const EditRoleDialog = ({ role, open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [roleDetails, setRoleDetails] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    role_scope: "subsidiary",
    level: 3,
    permissions: []
  });

  useEffect(() => {
    if (open && role) {
      fetchRoleDetails();
    }
  }, [open, role]);

  const fetchRoleDetails = async () => {
    if (!role) return;
    
    try {
      const response = await axios.get(`${API}/roles/${role.id}/details`);
      setRoleDetails(response.data);
      setFormData({
        name: response.data.name,
        description: response.data.description || "",
        role_scope: response.data.role_scope || "subsidiary",
        level: response.data.level || 3,
        permissions: response.data.permissions || []
      });
    } catch (error) {
      console.error("Error fetching role details:", error);
      toast.error("Failed to load role details");
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name) {
      toast.error("Please enter a role name");
      return;
    }
    
    if (formData.permissions.length === 0) {
      toast.error("Please select at least one permission");
      return;
    }
    
    setLoading(true);
    try {
      // If editing a system role, create a company-specific copy instead
      if (isSystemRole) {
        await axios.post(`${API}/roles`, {
          name: formData.name + " (Company Custom)",
          description: formData.description,
          role_scope: formData.role_scope,
          level: formData.level,
          permissions: formData.permissions,
          is_system_role: false
        });
        toast.success("Custom role created from system role");
      } else {
        // Update custom role directly
        await axios.put(`${API}/roles/${role.id}/permissions`, formData);
        toast.success("Role updated successfully");
      }
      
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error("Error updating role:", error);
      toast.error(error.response?.data?.detail || "Failed to update role");
    } finally {
      setLoading(false);
    }
  };

  if (!role || !roleDetails) {
    return null;
  }

  const isSystemRole = roleDetails.is_system_role;
  const userCount = roleDetails.user_count || 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5" />
            Edit Role: {roleDetails.name}
            {isSystemRole && (
              <Badge variant="secondary" className="ml-2">System Role</Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {isSystemRole 
              ? "Editing system role (requires platform admin privileges)"
              : "Update role details and permissions"
            }
          </DialogDescription>
        </DialogHeader>

        {/* Warning for system roles */}
        {isSystemRole && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">Editing System Role</p>
              <p className="mt-1">
                This is a system-defined role. When you save changes, a new <strong>company-specific custom role</strong> will be created 
                with your modified permissions. The original system role will remain unchanged.
              </p>
            </div>
          </div>
        )}

        {/* Assigned users warning */}
        {userCount > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">Active Role Assignment</p>
              <p className="mt-1">
                This role is currently assigned to <strong>{userCount} user{userCount !== 1 ? "s" : ""}</strong>.
                Changes to permissions will immediately affect all assigned users.
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Basic Information */}
          <div>
            <h3 className="font-medium text-sm mb-3 pb-2 border-b">Basic Information</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Role Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  required
                />
                {isSystemRole && (
                  <p className="text-xs text-blue-600 mt-1">
                    A new custom role will be created with this name + "(Company Custom)"
                  </p>
                )}
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleChange("description", e.target.value)}
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="role_scope">Role Scope</Label>
                  <Select
                    value={formData.role_scope}
                    onValueChange={(value) => handleChange("role_scope", value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="company">Company-wide</SelectItem>
                      <SelectItem value="subsidiary">Subsidiary-specific</SelectItem>
                      <SelectItem value="multi_subsidiary">Multi-Subsidiary</SelectItem>
                      <SelectItem value="department">Department-specific</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="level">Hierarchy Level</Label>
                  <Select
                    value={formData.level.toString()}
                    onValueChange={(value) => handleChange("level", parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Level 1 (Highest)</SelectItem>
                      <SelectItem value="2">Level 2</SelectItem>
                      <SelectItem value="3">Level 3</SelectItem>
                      <SelectItem value="4">Level 4</SelectItem>
                      <SelectItem value="5">Level 5 (Lowest)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          {/* Permissions */}
          <div>
            <h3 className="font-medium text-sm mb-3 pb-2 border-b flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Permissions Configuration
            </h3>
            <PermissionMatrix
              selectedPermissions={formData.permissions}
              onChange={(permissions) => handleChange("permissions", permissions)}
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading 
                ? (isSystemRole ? "Creating..." : "Updating...") 
                : (isSystemRole ? "Create Custom Role" : "Update Role")
              }
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditRoleDialog;
